/* eslint-disable vue/max-len */
/* eslint-disable max-len */

import { GetterTree, MutationTree, ActionTree } from 'vuex';
import { toMapById, toMapByField, cloneModel } from '@/model/model-utils';
import { deleteModelEntities, mergeSortedModelEntities } from '@/grpc-api/model/model-utils';
import { EmailMessageTypeChannel } from '@/model/MessageType';
import { BuiltInEventTypes } from '@/model/MessageEventType';

import grpcClient, { TemplatePreview } from '@/grpc-api/grpc-client';
import MessageSettings from '@/grpc-api/model/message-settings';
import MessageTranslation from '@/grpc-api/model/message-translation';
import MessageType from '@/grpc-api/model/message-type';
import EventType from '@/grpc-api/model/event-type';
import Campaign from '@/grpc-api/model/campaign';
import ReservationService from '@/grpc-api/model/reservation-service';
import Tab from '@/model/Tab';
import Handlebar from '@/grpc-api/model/handlebar';
import MessageRule from '@/grpc-api/model/message-rule';
import IRootState, { IMessageCenterState } from './store-state';

export class MessageCenterState implements IMessageCenterState {
  settings: MessageSettings | null = null;

  translations: MessageTranslation[] = [];

  messageTypes: MessageType[] = [];

  eventTypes: EventType[] = [];

  campaigns: Campaign[] = [];

  services: ReservationService[] = [];

  tabs: Tab[] = [];

  handlebars: Handlebar[] = [];

  rules: MessageRule[] = [];

  isLoaded: boolean = false;
}

const mutations = <MutationTree<IMessageCenterState>>{
  RESET(state: MessageCenterState) {
    Object.assign(state, new MessageCenterState());
  },
  RESET_MESSAGE_CENTER(state: MessageCenterState) {
    Object.assign(state, new MessageCenterState());
  },
  UPDATE_MESSAGE_CENTER(
    state: MessageCenterState,
    p: {
      settings: MessageSettings, translations: MessageTranslation[],
      messageTypes: MessageType[], eventTypes: EventType[],
      campaigns: Campaign[], services: ReservationService[], tabs: Tab[],
      handlebars: Handlebar[],
    },
  ) {
    state.settings = p.settings;
    state.translations = p.translations;

    state.eventTypes = p.eventTypes;
    state.campaigns = p.campaigns;
    state.services = p.services;
    state.tabs = p.tabs;
    state.messageTypes = p.messageTypes;
    state.handlebars = p.handlebars;

    console.log('MC settings:', state.settings);
    console.log('MC translations:', state.translations);
    console.log('MC messageTypes:', state.messageTypes);
    console.log('MC eventTypes:', state.eventTypes);
    console.log('MC campaigns:', state.campaigns);
    console.log('MC services:', state.services);
    console.log('MC tabs:', state.tabs);
    console.log('MC handlebars:', state.handlebars);
  },
  UPDATE_MESSAGE_PREFERENCES(state: MessageCenterState, p: { settings: MessageSettings, translations: MessageTranslation[] }) {
    state.settings = p.settings;
    state.translations = p.translations;

    console.log('MC settings:', state.settings);
    console.log('MC translations:', state.translations);
  },
  UPDATE_MESSAGE_RULES(state: MessageCenterState, p: { rules: MessageRule[] }) {
    deleteModelEntities(state.rules, p.rules);
    const sortedRules = p.rules.sort(MessageRule.compare);
    console.log('MC sorted rules:', sortedRules);
    mergeSortedModelEntities(state.rules, sortedRules, MessageRule.compare);
    console.log('MC rules:', state.rules);
  },
  SET_MESSAGE_PREFERENCES_LOADED(state: MessageCenterState) {
    state.isLoaded = true;
  },
};

const actions = <ActionTree<IMessageCenterState, IRootState>>{
  async resetMessageCenter({
    state, commit, dispatch, rootGetters,
  }) {
    commit('RESET_MESSAGE_CENTER');
  },
  async loadMessageCenter({
    state, commit, dispatch, rootGetters,
  }) {
    if (!rootGetters.isLoaded) return;

    const messageCenter = await grpcClient.getMessageCenter(rootGetters);
    commit('UPDATE_MESSAGE_CENTER', messageCenter);

    const rules = await grpcClient.listMessageRules(rootGetters);
    commit('UPDATE_MESSAGE_RULES', { rules });

    commit('SET_MESSAGE_PREFERENCES_LOADED');
  },
  async saveMessageCenterPreferences({ commit, state, rootGetters }, p: { settings: MessageSettings, translations: MessageTranslation[]}) {
    const { settings, translations } = await grpcClient.updateMessageCenterPreferences({ settings: p.settings, translations: p.translations });
    commit('UPDATE_MESSAGE_PREFERENCES', { settings, translations });

    // settings shape the server-resolved template list (tone re-translates builtin '*'
    // bodies in getMessageRule, locales drive the per-locale rows), so any already-loaded
    // rules are potentially stale after a save -> re-list. Rules come back without
    // templates, and the rule editor re-fetches the full rule (fresh translations) on open.
    const rules = await grpcClient.listMessageRules(rootGetters);
    commit('UPDATE_MESSAGE_RULES', { rules });
  },
  async messageCenterGetRule({ commit, state, rootGetters }, p: { id: number }) {
    const rule = await grpcClient.getMessageRule(p.id, rootGetters);
    commit('UPDATE_MESSAGE_RULES', { rules: [rule] });
    return rule;
  },
  async messageCenterUpdateRule({ state, commit, rootGetters }, p: { rule: MessageRule, delete: boolean }) {
    let rule = cloneModel(p.rule);
    rule.isDeleted = p.delete;
    rule = await grpcClient.updateMessageRule(rule, rootGetters);
    commit('UPDATE_MESSAGE_RULES', { rules: [rule] });
    return rule;
  },
  // locale priority mode onboarding: enter draft / promote / cancel / revert (TESTING ONLY)
  async setLocalePriorityMode({ commit, rootGetters }, p: { action: 'enter' | 'promote' | 'cancel' | 'revert' }) {
    const preferences = await grpcClient.setLocalePriorityMode(p.action, rootGetters);
    commit('UPDATE_MESSAGE_PREFERENCES', { settings: preferences.settings, translations: preferences.translations });

    const rules = await grpcClient.listMessageRules(rootGetters);
    commit('UPDATE_MESSAGE_RULES', { rules });
  },
  // transient template preview from synthetic data (no state mutation)
  async messageCenterPreviewTemplate(_ctx, p: { messageTypeId: number, eventTypeId: number, locale: string, body: string, subject: string, serviceId?: number }): Promise<TemplatePreview> {
    return grpcClient.previewTemplate(p);
  },
};

const getters = <GetterTree<IMessageCenterState, IRootState>>{
  // messageCenterCampaigns(state: MessageCenterState) { return state.campaigns; },
  // messageCenterServices(state: MessageCenterState) { return state.services; },
  // messageCenterTabs(state: MessageCenterState) { return state.tabs; },

  messageCenterMessageTypes(state: MessageCenterState): MessageType[] { return state.messageTypes; },
  messageCenterMessageTypesById(state: MessageCenterState) { return toMapById(state.messageTypes); },
  messageCenterEventTypesById(state: MessageCenterState) { return toMapById(state.eventTypes); },
  messageCenterCampaignsById(state: MessageCenterState) { return toMapById(state.campaigns); },
  messageCenterServicesById(state: MessageCenterState) { return toMapById(state.services); },
  messageCenterTabsById(state: MessageCenterState) { return toMapById(state.tabs); },
  messageCenterHandlebarsById(state: MessageCenterState) { return toMapById(state.handlebars); },

  messageCenterEventTypes(state: MessageCenterState): EventType[] { return state.eventTypes; },
  messageCenterEventTypesByType(state: MessageCenterState) { return toMapByField(state.eventTypes, 'type'); },

  messageCenterBuiltinRules(state: MessageCenterState, localGetters: any, rootState: any, rootGetters: any): MessageRule[] {
    const rules = BuiltInEventTypes.flatMap((type, index) => {
      const emailMessageType = state.messageTypes.find((mt) => mt.messageType === EmailMessageTypeChannel);

      let rule = state.rules.find((o) => o.isDefault && o.eventType?.type === type);
      if (rule) {
        // rule.templates = rule.templates.filter((t) => localGetters.messageCenterEnabledLocalesSet.has(t.locale));
        return rule;
      }

      const eventType = localGetters.messageCenterEventTypesByType.get(type);
      if (!eventType) return [];

      rule = new MessageRule();
      rule.id = -(index + 1000);
      rule.eventType = eventType;
      // rule.isDefault = true;
      rule.isInactive = true;
      rule.messageType = emailMessageType;
      return rule;
    });

    // rules marked default only from backend
    // const missingDefaultRules = state.rules.filter((o) => o.isDefault && !rules.includes(o));

    // filter out waitlist-related rules if waitlist is not enabled
    const wlOn = (rootState.settings.accountSettings.enableWaitlist && rootGetters.isWaitlistFeatureAllowed) as Boolean;
    const wlFilteredRules = rules.filter((r) => r.eventType?.isWaitlist !== true || wlOn);

    return wlFilteredRules;
  },
  messageCenterCustomRules(state: MessageCenterState, localGetters: any, rootState: any, rootGetters: any): MessageRule[] {
    const rules = [
      ...state.rules.filter((o) => !o.isDefault && !o.isInactive),
      ...state.rules.filter((o) => !o.isDefault && o.isInactive),
    ];
    return rules;
  },
  isMessageCenterLoaded(state: MessageCenterState, localGetters: any, rootState: any, rootGetters: any): boolean {
    return state.isLoaded && rootGetters.isLoaded;
  },
  messageCenterEnabledLocales: (state: MessageCenterState, localGetters: any, rootState: IRootState, rootGetters: any) => state.settings?.enabledLocales ?? [],
  messageCenterEnabledLocalesSet(state: MessageCenterState) { return new Set(state.settings?.enabledLocales ?? []); },
  messageCenterSettings: (state: MessageCenterState): MessageSettings | null => state.settings,
  messageCenterTranslations: (state: MessageCenterState): MessageTranslation[] => state.translations,
  messageCenterIsLocalePriorityMode: (state: MessageCenterState): boolean => state.settings?.communicationFlags?.includes('locale_priority_mode') ?? false,
  // step-1 onboarding: new templates are prepared in the draft table while the old ones keep sending
  messageCenterIsLocalePriorityDraftMode: (state: MessageCenterState): boolean => state.settings?.communicationFlags?.includes('locale_priority_draft') ?? false,
};

const MessageCenterStore = {
  namespaced: false,
  state: new MessageCenterState(),
  mutations,
  actions,
  getters,
};

export default MessageCenterStore;
